import React, {useRef} from 'react';
import {isEqual} from 'lodash';
import {Button, Card, Dropdown, Icon, MenuListItem, Toggle, WithToggle} from '@albert-io/atomic';

import {Dimension, MetricColumn, QueryVariables, TopLevelFilter} from '../reports.types';
import {METRIC_COLUMNS, columnIsAvailable} from '../reports.utils';

import './metrics-selector.scss';

const getDimensionsTitle = (dimensions: Dimension[]) => {
  if (isEqual(dimensions, ['students'])) {
    return 'Student';
  }
  if (isEqual(dimensions, ['assignments'])) {
    return 'Assignment';
  }
  if (isEqual(dimensions, ['standards'])) {
    return 'Standard';
  }
  if (isEqual(dimensions, ['questions'])) {
    return 'Question';
  }
  if (isEqual(dimensions, ['students', 'assignments'])) {
    return 'Gradebook';
  }
  return '';
};

interface Props {
  dimensions: Dimension[];
  filters: QueryVariables;
  topLevelFilterType: TopLevelFilter;
  isSelected: (option: MetricColumn) => boolean;
  onSelect: (option: MetricColumn) => void;
}

export const MetricsSelector = ({
  dimensions,
  filters,
  isSelected,
  onSelect,
  topLevelFilterType
}: Props) => {
  const toggleRef = useRef(null);
  const includedOptions = METRIC_COLUMNS.filter((o) =>
    columnIsAvailable(o, dimensions, filters, topLevelFilterType)
  );
  const excludedOptions = METRIC_COLUMNS.filter(
    (o) => !columnIsAvailable(o, dimensions, filters, topLevelFilterType)
  );

  const renderOption = (option: MetricColumn, disabled: boolean) => (
    <Option
      option={option}
      dimensions={dimensions}
      variables={filters}
      disabled={disabled}
      selected={isSelected(option)}
      onSelect={onSelect}
      key={option.key}
    />
  );

  const availableMetricsTitle = `${getDimensionsTitle(dimensions)} Metrics`;

  return (
    <WithToggle>
      {({on, onClick}) => (
        <>
          <Button color='secondary' variant='outlined' onClick={onClick} ref={toggleRef}>
            Metric Options
            <Icon icon='eye' className='u-mar-l_1' />
          </Button>
          {on ? (
            <Dropdown.Tray
              className='metrics-selector__dropdown-tray'
              positionFixed
              placement='bottom-end'
              target={toggleRef.current}
            >
              <Card paddingLevel={1}>
                {includedOptions.length > 0 ? (
                  <MenuListItem
                    className='u-justify-content_flex-start'
                    clickable={false}
                    hoverable={false}
                  >
                    <MenuListItem.CategoryItemText
                      size='s'
                      className='u-font-weight_normal u-color_slate-600'
                    >
                      {availableMetricsTitle}
                    </MenuListItem.CategoryItemText>
                  </MenuListItem>
                ) : null}
                {includedOptions.map((o) => renderOption(o, false))}

                {excludedOptions.length > 0 ? (
                  <MenuListItem
                    className='u-justify-content_flex-start'
                    clickable={false}
                    hoverable={false}
                  >
                    <MenuListItem.CategoryItemText
                      size='s'
                      className='u-font-weight_normal u-mar-t_2 u-color_slate-600'
                    >
                      Unavailable For This View
                    </MenuListItem.CategoryItemText>
                  </MenuListItem>
                ) : null}
                {excludedOptions.map((o) => renderOption(o, true))}
              </Card>
            </Dropdown.Tray>
          ) : null}
        </>
      )}
    </WithToggle>
  );
};

const Option = ({
  option,
  dimensions,
  variables,
  disabled,
  selected,
  onSelect
}: {
  option: MetricColumn;
  dimensions: Dimension[];
  variables: QueryVariables;
  disabled: boolean;
  selected: boolean;
  onSelect: (option: MetricColumn) => void;
}) => {
  const TextComponent = disabled ? MenuListItem.CategoryItemText : MenuListItem.Text;
  return (
    <MenuListItem
      className='u-justify-content_flex-start u-pad_1'
      hoverable={!disabled}
      clickable={!disabled}
      onMenuItemClick={() => {
        if (!disabled) {
          onSelect(option);
        }
      }}
    >
      <Toggle
        className='u-mar-r_2'
        checked={selected && !disabled} // show as "off" if this column is not available
        disabled={disabled}
        onChange={() => onSelect(option)}
      />
      <TextComponent>
        {option.metricType === 'default' ? (
          option.title
        ) : (
          <option.renderTitle dimensions={dimensions} variables={variables} />
        )}
      </TextComponent>
    </MenuListItem>
  );
};
